import { FeatureFlag } from '../CommonSelectors/CommonArea.types';

export enum PdfSectionType {
  CoverPage = 'CoverPage',
  SectionTitle = 'SectionTitle',
  Disclosures = 'Disclosures',
  Summary = 'Summary',
  ActionSteps = 'ActionSteps',
  NetworthSummary = 'NetWorthSummary',
  NetworthDetailed = 'NetWorthDetailed',
  ProjectedCashflow = 'ProjectedCashFlow',
  ProjectedBalanceSheet = 'ProjectedBalanceSheet',
  RetirementAssets = 'RetirementAssets',
  RetirementIncome = 'RetirementExpenses',
  RetirementMonteCarlo = 'RetirementMonteCarlo',
  EmergencyFund = 'EmergencyFundGoal',
  Education = 'EducationGoals',
  LifeInsurance = 'LifeInsurance',
  DisabilityInsurance = 'DisabilityInsurance',
  LongTermCareInsurance = 'LongTermCareInsurance',
  BuyAHouse = 'BuyAHouseGoal',
  LargePurchase = 'LargePurchaseGoals',
  DebtManagement = 'DebtManagementGoal',
  AdvancedReport = 'AdvancedReport',
  AssetAllocation = 'AssetAllocation',
  HealthCare = 'HealthCareGoal',
  Assumptions = 'Assumptions',
  GoalSummary = 'GoalSummary',
  EstatePlanning = 'EstatePlanningGoal',
  EstatePlanningGoalBeneficiaries = 'EstatePlanningGoalBeneficiaries',
  RiskToleranceWorkflow = 'RiskToleranceWorkflow',
  ComparePlans = 'ComparePlans',
  RiskProfile3d = 'RiskProfile3d',
  ProtectLiveDream = 'ProtectLiveDream',
  BeFi20 = 'BeFi20',
  PulseCheck = 'PulseCheck',
  EstatePlanningFlow = 'EstatePlanningFlow',
  InsuranceSummary = 'InsuranceSummary',
}

export interface PdfCommonSection {
  type: Exclude<
    PdfSectionType,
    | PdfSectionType.CoverPage
    | PdfSectionType.SectionTitle
    | PdfSectionType.LifeInsurance
    | PdfSectionType.AdvancedReport
    | PdfSectionType.ComparePlans
    | PdfSectionType.BeFi20
    | PdfSectionType.ProtectLiveDream
  >;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
}

export interface PdfCoverSection {
  type: PdfSectionType.CoverPage;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    title: string;
    signature: string;
    date: string;
  };
  coverPageName: string;
}

export interface PdfSectionTitleSection {
  type: PdfSectionType.SectionTitle;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    title: string;
    body: string;
  };
}

export interface PdfLifeInsuranceSection {
  type: PdfSectionType.LifeInsurance;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    recommendations: string[];
    owners: { id: number; name: string }[];
    selectedRecommendations: string[];
    selectedOwners: number;
  };
}

export interface PdfAdvancedReportSection {
  type: PdfSectionType.AdvancedReport;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    selectedReport: string;
  };
}

export interface PdfComparePlansSection {
  type: PdfSectionType.ComparePlans;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields: {
    plans: { id: string; name: string }[];
    selectedPlans: string[];
  };
}

export interface PdfBeFi20Section {
  type: PdfSectionType.BeFi20;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields?: {
    plans: { id: string; name: string; similarity: 'Low' | 'Varied' | 'High' }[];
    selectedComparisons: string[];
  };
}

export interface AssetBucketingSection {
  type: PdfSectionType.ProtectLiveDream;
  title: string;
  nonExcludable: boolean;
  hidden: boolean;
  editableFields?: {
    showFullBreakdown: boolean;
  };
}

export type PdfSection =
  | PdfCoverSection
  | PdfSectionTitleSection
  | PdfLifeInsuranceSection
  | PdfAdvancedReportSection
  | PdfComparePlansSection
  | PdfBeFi20Section
  | AssetBucketingSection
  | PdfCommonSection;

export interface PdfModel {
  includedSections: PdfSection[];
  excludedSections: PdfSection[];
}

export interface WhiteLabelData {
  logo: string;
  title: string;
  inlineImgStyle: string;
}

export interface PdfReducer {
  client: any;
  loadedComponents: PdfSectionType[];
  loadedAdvancedReportsSections: string[];
  featureFlags: FeatureFlag[];
}
